import { Input, Stack, Text } from "@mantine/core";
import { ChangeEvent, type JSX, useEffect, useMemo, useRef, useState } from "react";

import type { CustomerDetailsPayload } from "../../../../types";

import { RadioCard } from "../../../../shared-utils/common-ui/radio-card";
import classes from "./CustomerVerification.module.css";

const CustomerType = Object.freeze({
    Primary: "Primary",
    Authorized: "Authorized",
    Custom: "Someone else",
});

type CustomerType = (typeof CustomerType)[keyof typeof CustomerType];

interface CustomerProps {
    name: string;
    type: CustomerType;
}

interface CustomerNamesSectionProps {
    customer?: CustomerDetailsPayload;
    customerNameFromIVR?: string;
    onOptionSelect: (input?: string) => void;
    isDisabled: boolean;
    /**
     * Required key prop to reset component state when needed.
     * Pass a unique value (like session ID) to reset selected state.
     */
    key: string | number;
}

const SOMEONE_ELSE = "Someone else";

export function CustomerNamesSection({
    customer,
    customerNameFromIVR,
    onOptionSelect,
    isDisabled,
}: CustomerNamesSectionProps): JSX.Element {
    const [selected, setSelected] = useState("");
    const [inputValue, setInputValue] = useState(customerNameFromIVR);
    const focusRef = useRef<HTMLInputElement>(null);

    const onRadioButtonSelected = (name: string) => {
        onOptionSelect(undefined);
        setSelected(name);

        if (name === SOMEONE_ELSE) {
            // This puts the focus operation after the render cycle ensuring the input is available in the DOM
            setTimeout(() => focusRef.current?.focus(), 0);
        } else {
            // For any other option, just pass the selected name to the parent
            onOptionSelect(name);
        }
    };

    // Sync the IVR customer name to the store on mount so the confirm button is enabled
    useEffect(() => {
        if (customerNameFromIVR) {
            onOptionSelect(customerNameFromIVR);
        }
        // -- cleanup on unmount: handles edge case allowing the user to select a name
        // and then change the customer mdn without having to reselect a name.
        return () => onOptionSelect(undefined);
        // oxlint-disable-next-line exhaustive-deps
    }, []); // onOptionSelect changes often and causes unintended behavior if included in the dependency array

    const onInputChange = ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) => {
        setInputValue(value);
        onOptionSelect(value);
    };

    const customerProps = useMemo<CustomerProps[]>(() => {
        if (!customer?.primaryAccountHolder?.fullName) {
            return [];
        }

        const primaryAccountHolder = {
            name: customer.primaryAccountHolder.fullName,
            type: CustomerType.Primary,
        };

        const authorizedUsers = customer.authorizedUsers
            ? customer.authorizedUsers
                  .filter((user) => !!user.fullName)
                  .map((user) => ({
                      name: user.fullName!,
                      type: CustomerType.Authorized,
                  }))
            : [];

        return [primaryAccountHolder, ...authorizedUsers, { name: SOMEONE_ELSE, type: CustomerType.Custom }];
    }, [customer]);

    // ! handles the case where customer name is passed from ivr and expert does not need to make any additional selection or edits.
    useEffect(() => {
        if (customerNameFromIVR) {
            onOptionSelect(customerNameFromIVR);
            setSelected(customerNameFromIVR);
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [customerNameFromIVR]); // onOptionSelect changes often and causes unintended behavior if included in the dependency array

    return (
        <>
            <Text c="dark.1" size="sm" mb="md" mt="xxl" id="customer-name-question">
                What’s their name?
            </Text>

            {customerProps.length > 0 && (
                <Stack gap={"xs"} mb="xs" role="radiogroup" aria-labelledby="customer-name-question">
                    {customerProps.map((item, index) => {
                        return (
                            <RadioCard
                                disabled={isDisabled}
                                className={`${selected === item.name ? classes.radioSelected : classes.radio} fs-mask`}
                                color="var(--mantine-color-dark-5)"
                                onClick={() => {
                                    onRadioButtonSelected(item.name);
                                }}
                                selected={selected === item.name}
                                key={item.name}
                                aria-label={item.type}
                                id={`customer-name-${index}`}
                                label={item.type === CustomerType.Custom ? item.name : item.name.toLowerCase()}
                                value={item.name}
                                analytics={{
                                    actionId: "CustomerNameSelected",
                                }}
                                radioProps={{ tt: item.type === CustomerType.Custom ? "none" : "capitalize" }}
                            />
                        );
                    })}
                </Stack>
            )}

            {(selected as CustomerType) === CustomerType.Custom || customerProps.length === 0 ? (
                <Input
                    aria-label="Enter customer name"
                    aria-describedby="customer-name-question"
                    ref={focusRef}
                    id="customer-name-input"
                    disabled={isDisabled}
                    mt="0px"
                    mb="0px"
                    size="md"
                    onChange={onInputChange}
                    className="fs-mask"
                    classNames={{ wrapper: classes.styledInputWrapper, input: classes.styledInput }}
                    placeholder="Enter name"
                    value={inputValue}
                />
            ) : null}
        </>
    );
}
